import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MultiSelect } from 'primereact/multiselect';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import {
    useLazyGetInvoiceApprovalHistoryQuery,
    useLazyExportInvoiceApprovalHistoryQuery
} from '../../../../../api/endpoints/invoiceApproval';
import InvoiceApprovalHistoryFilter from './InvoiceHistoryFilter';
import GenericFileExportButton from '../../../../../components/common/GenericFileExportButton';
import { formatDateForApi } from '../../../../../utils/dateUtils';
import { PAGE_SIZE_OPTIONS } from '../../../../../utils/tableConstants';

const HistoryPageModal = ({ visible, onHide }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const dt = useRef(null);

    // Pagination
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 0 });

    // Client-side sort
    const [sortField, setSortField] = useState('invoiceApprovedDate');
    const [sortOrder, setSortOrder] = useState(-1);

    // Applied filters from GenericTableFilter
    const [appliedFilters, setAppliedFilters] = useState(() => {
        const now = new Date();
        const startDate = formatDateForApi(new Date(now.getFullYear(), now.getMonth(), 1));
        const endDate = formatDateForApi(new Date(now.getFullYear(), now.getMonth() + 1, 0));
        return { startDate, endDate };
    });

    // Column visibility
    const [visibleColumns, setVisibleColumns] = useState([
        'warrClaimsNo', 'dmsOrderNumber', 'warrType', 'chassisNo',
        'currency', 'totalPrice', 'invoiceCreatedDate', 'invoiceApprovedDate'
    ]);

    // API hooks
    const [fetchHistory, { data: historyResponse, isLoading, isError }] = useLazyGetInvoiceApprovalHistoryQuery();

    const historyData = historyResponse?.data?.items || [];
    const totalRecords = historyResponse?.data?.totalCount || 0;

    // Client-side sorting
    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return historyData;
        return [...historyData].sort((a, b) => {
            let aV = a?.[sortField], bV = b?.[sortField];
            if (aV == null && bV == null) return 0;
            if (aV == null) return sortOrder;
            if (bV == null) return -sortOrder;
            if (typeof aV === 'string' && typeof bV === 'string') { aV = aV.toLowerCase(); bV = bV.toLowerCase(); }
            if (sortField.toLowerCase().includes('date')) { aV = new Date(aV); bV = new Date(bV); }
            if (typeof aV === 'number' && typeof bV === 'number') return sortOrder * (aV - bV);
            if (aV < bV) return -sortOrder;
            if (aV > bV) return sortOrder;
            return 0;
        });
    }, [historyData, sortField, sortOrder]);

    // Export params
    const exportParams = useMemo(() => ({
        ...appliedFilters,
        selectedColumns: visibleColumns,
        LanguageId: getCurrentLanguageId(),
        compressionType: 0
    }), [appliedFilters, visibleColumns, getCurrentLanguageId]);

    const handleSort = useCallback((e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); }, []);

    // Load data
    const loadData = useCallback(async (page = 1, size = 10, extraFilters = {}) => {
        await fetchHistory({ pageNumber: page, pageSize: size, ...extraFilters });
    }, [fetchHistory]);

    // On modal open or filter/page change
    React.useEffect(() => {
        if (visible) {
            loadData(lazyParams.page + 1, lazyParams.rows, appliedFilters);
        }
    }, [visible, lazyParams, appliedFilters, loadData]);

    const onPageChange = useCallback((e) => setLazyParams(e), []);

    // Filter callbacks from InvoiceApprovalHistoryFilter
    const handleApplyFilters = useCallback((filterValues) => {
        const f = {};

        const dealerPayload = filterValues.dealer;
        if (dealerPayload && !dealerPayload.isAllDealers && dealerPayload.dealerIds?.length > 0) {
            f.bosBranchCodes = dealerPayload.dealerIds.map(String);
        }
        if (filterValues.chassisNo) f.chassisNo = filterValues.chassisNo;
        if (filterValues.dmsOrderNumber) f.dmsOrderNumber = filterValues.dmsOrderNumber;
        if (filterValues.startDate) f.startDate = filterValues.startDate instanceof Date ? formatDateForApi(filterValues.startDate) : filterValues.startDate;
        if (filterValues.endDate) f.endDate = filterValues.endDate instanceof Date ? formatDateForApi(filterValues.endDate) : filterValues.endDate;

        setAppliedFilters(f);
        setLazyParams({ first: 0, rows: 10, page: 0 });
    }, []);

    const handleResetFilters = useCallback(() => {
        const now = new Date();
        const startDate = formatDateForApi(new Date(now.getFullYear(), now.getMonth(), 1));
        const endDate = formatDateForApi(new Date(now.getFullYear(), now.getMonth() + 1, 0));
        setAppliedFilters({ startDate, endDate });
        setLazyParams({ first: 0, rows: 10, page: 0 });
    }, []);

    // CSV export
    const exportCSV = () => dt.current?.exportCSV();

    // Formatters
    const fmtCurrency = useCallback((amt, cur = 'TRY') => {
        if (amt == null || isNaN(amt)) return '-';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur || 'TRY' }).format(amt);
    }, []);
    const fmtDateTime = useCallback((ds) => {
        if (!ds) return '-';
        return new Date(ds).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    }, []);

    // Column body templates
    const statusBodyTemplate = useCallback((row) => {
        const map = {
            0: { label: th('invoiceApproval.status.pending'), sev: 'warning' },
            1: { label: th('invoiceApproval.status.approved'), sev: 'success' },
            2: { label: th('invoiceApproval.status.rejected'), sev: 'danger' }
        };
        const s = map[row.invoiceStatus] || { label: th('invoiceApproval.status.unknown'), sev: 'info' };
        return <Tag value={s.label} severity={s.sev} />;
    }, [th]);

    const activeBodyTemplate = useCallback((row) => (
        <i className={`pi ${row.isActive ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'}`}
            title={row.isActive ? th('common.active') : th('common.passive')} />
    ), [th]);

    // All available columns
    const columns = [
        { field: 'warrClaimsNo', header: th('invoiceApproval.table.warrantyClaimNo'), sortable: true, frozen: true, style: { minWidth: '150px' } },
        { field: 'dealerName', header: th('invoiceApproval.table.dealerName'), sortable: true, style: { minWidth: '200px' } },
        { field: 'invoiceNumber', header: th('invoiceApproval.table.invoiceNumber'), sortable: true, style: { minWidth: '140px' } },
        { field: 'chassisNo', header: th('invoiceApproval.detailModal.chassisNo'), sortable: true, style: { minWidth: '160px' } },
        { field: 'dmsOrderNumber', header: th('invoiceApproval.history.dmsOrderNumber'), sortable: true, style: { minWidth: '140px' } },
        { field: 'warrType', header: th('invoiceApproval.table.warrantyType'), sortable: true, style: { minWidth: '120px' } },
        { field: 'totalPrice', header: th('invoiceApproval.history.totalPrice'), sortable: true, body: (r) => fmtCurrency(r.totalPrice, r.currency), style: { minWidth: '140px', textAlign: 'right' } },
        { field: 'currency', header: th('invoiceApproval.detailModal.currency'), sortable: true, style: { minWidth: '100px', textAlign: 'center' } },
        { field: 'bosBranchCode', header: th('invoiceApproval.table.branchCode'), sortable: true, style: { minWidth: '120px' } },
        { field: 'bosBranchSiteCode', header: th('invoiceApproval.table.branchSiteCode'), sortable: true, style: { minWidth: '140px' } },
        { field: 'invoiceStatus', header: th('invoiceApproval.table.invoiceStatus'), sortable: true, body: statusBodyTemplate, style: { minWidth: '130px' } },
        { field: 'invoiceCreatedDate', header: th('invoiceApproval.history.invoiceCreatedDate'), sortable: true, body: (r) => fmtDateTime(r.invoiceCreatedDate), style: { minWidth: '170px' } },
        { field: 'invoiceApprovedDate', header: th('invoiceApproval.history.invoiceApprovedDate'), sortable: true, body: (r) => fmtDateTime(r.invoiceApprovedDate), style: { minWidth: '170px' } },
        { field: 'createdDate', header: th('invoiceApproval.history.createdDate'), sortable: true, body: (r) => fmtDateTime(r.createdDate), style: { minWidth: '170px' } },
        { field: 'modifiedDate', header: th('invoiceApproval.history.modifiedDate'), sortable: true, body: (r) => fmtDateTime(r.modifiedDate), style: { minWidth: '170px' } },
        { field: 'createdName', header: th('invoiceApproval.history.createdBy'), sortable: true, style: { minWidth: '140px' } },
        { field: 'modifiedName', header: th('invoiceApproval.history.modifiedBy'), sortable: true, style: { minWidth: '140px' } },
        { field: 'isActive', header: th('invoiceApproval.history.isActive'), sortable: true, body: activeBodyTemplate, style: { minWidth: '100px', textAlign: 'center' } },
        { field: 'note', header: th('invoiceApproval.history.note'), sortable: true, style: { minWidth: '200px' } }

    ];

    const renderDialogFooter = () => (
        <div className="flex justify-content-between align-items-center">
            <span className="text-sm text-gray-500">
                {th('invoiceApproval.history.totalRecords') || 'Toplam Kayıt'}: {totalRecords}
            </span>
            <Button label={th('invoiceApproval.history.close')} icon="pi pi-times" outlined onClick={onHide} severity="secondary" />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '95vw', maxWidth: '1600px' }}
            modal
            footer={renderDialogFooter()}
            onHide={onHide}
            maximizable
            breakpoints={{ '960px': '95vw', '641px': '100vw' }}
        >
            <div className="flex flex-column gap-3 mb-3">
                {/* Title */}
                <div className="text-xl font-semibold">{th('invoiceApproval.history.title')}</div>

                {/* Filter */}
                <InvoiceApprovalHistoryFilter
                    handleApplyFilters={handleApplyFilters}
                    handleResetFilters={handleResetFilters}
                />

                {/* Toolbar */}
                <div className="flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center gap-2">
                    <MultiSelect
                        value={visibleColumns}
                        options={columns.map((c) => ({ label: c.header, value: c.field }))}
                        onChange={(e) => setVisibleColumns(e.value)}
                        optionLabel="label"
                        placeholder={th('invoiceApproval.history.selectColumns') || 'Kolon seçiniz'}
                        className="w-full sm:w-20rem"
                        display="chip"
                        maxSelectedLabels={3}
                        selectedItemsLabel={th('invoiceApproval.history.selectedItems') || '{0} kolon seçildi'}
                    />
                    <div className="flex gap-2 flex-shrink-0 align-items-center">
                        <GenericFileExportButton
                            useExportQuery={useLazyExportInvoiceApprovalHistoryQuery}
                            exportParams={exportParams}
                            buttonLabel={th('invoiceApproval.history.exportExcel') || 'Excel'}
                            buttonClassName="p-button-outlined"
                            icon="pi pi-file-excel"
                            disabled={isLoading || !totalRecords}
                            fileNameExtractor={(result) => result?.fileName || `fatura-onay-gecmisi-${new Date().toISOString().split('T')[0]}.xlsx`}
                            dataExtractor={(result) => result?.fileBase64}
                            contentTypeExtractor={(result) => result?.contentType}
                            exportFormat="base64"
                        />
                        <Button
                            icon="pi pi-download"
                            label={th('invoiceApproval.history.csv') || 'CSV'}
                            onClick={exportCSV}
                            outlined
                            severity="secondary"
                            size="small"
                        />
                        <Button
                            icon="pi pi-refresh"
                            onClick={() => loadData(lazyParams.page + 1, lazyParams.rows, appliedFilters)}
                            disabled={isLoading}
                            outlined
                            severity="secondary"
                            size="small"
                            tooltip={th('common.refresh') || 'Yenile'}
                        />
                    </div>
                </div>
            </div>

            <Divider className="my-2" />

            {/* Content */}
            {isLoading && (
                <div className="flex justify-content-center align-items-center p-4">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {isError && (
                <div className="flex flex-column align-items-center justify-content-center p-4 text-red-500">
                    <i className="pi pi-exclamation-triangle text-4xl mb-2"></i>
                    <p>{th('invoiceApproval.history.loadError')}</p>
                    <Button
                        label={th('common.retry') || 'Tekrar Dene'}
                        icon="pi pi-refresh"
                        onClick={() => loadData(lazyParams.page + 1, lazyParams.rows, appliedFilters)}
                        outlined size="small" className="mt-2"
                    />
                </div>
            )}

            {!isLoading && !isError && historyData.length === 0 && (
                <div className="flex flex-column align-items-center justify-content-center p-6">
                    <i className="pi pi-inbox text-5xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 text-lg">{th('invoiceApproval.history.noRecords')}</p>
                </div>
            )}

            {!isLoading && !isError && historyData.length > 0 && (
                <DataTable
                    ref={dt}
                    value={sortedData}
                    paginator
                    rows={lazyParams.rows}
                    first={lazyParams.first}
                    totalRecords={totalRecords}
                    onPage={onPageChange}
                    onSort={handleSort}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    className="p-datatable-gridlines"
                    showGridlines stripedRows
                    emptyMessage={th('invoiceApproval.history.noRecords')}
                    resizableColumns columnResizeMode="expand"
                    scrollable scrollHeight="flex"
                    size="small" lazy dataKey="id"
                    exportFilename={`fatura-onay-gecmisi-${new Date().toISOString().split('T')[0]}`}
                    currentPageReportTemplate="{first} - {last} / {totalRecords}"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    sortMode="single"
                >
                    {columns
                        .filter((c) => visibleColumns.includes(c.field))
                        .map((c) => (
                            <Column key={c.field} field={c.field} header={c.header} body={c.body} sortable={c.sortable} frozen={c.frozen} style={c.style} />
                        ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default HistoryPageModal;
